import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiBriefcase,
  FiBookOpen,
  FiUser,
} from "react-icons/fi";
import AxiosInstance from "../Home/AxiosInstance";
import TraineeHeader from "./TraineeHeader";
import Footers from "../Home/Footers";
import "../Home/theme.css";
import "./TraineeProfile.css";

export default function TraineeCareerMatches() {
  const catalogState = { from: "career-matches" };
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState({
    has_preference: false,
    jobs: [],
    internships: [],
    trainings: [],
    message: "",
  });

  useEffect(() => {
    const traineeId = localStorage.getItem("traineeId");
    if (!traineeId) {
      alert("Trainee ID is missing. Please log in.");
      setLoading(false);
      return;
    }

    AxiosInstance.get(
      `companies/career-preference/${traineeId}/matches/?limit=50`
    )
      .then((res) => {
        setMatches({
          has_preference: Boolean(res.data?.has_preference),
          jobs: Array.isArray(res.data?.jobs) ? res.data.jobs : [],
          internships: Array.isArray(res.data?.internships)
            ? res.data.internships
            : [],
          trainings: Array.isArray(res.data?.trainings)
            ? res.data.trainings
            : [],
          message: res.data?.message || "",
        });
      })
      .catch((err) => {
        console.error("Error fetching career matches:", err);
        setMatches({
          has_preference: false,
          jobs: [],
          internships: [],
          trainings: [],
          message: "",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const empty =
    !matches.jobs.length &&
    !matches.internships.length &&
    !matches.trainings.length;

  return (
    <div className="TraineePage als-trainee-dash">
      <TraineeHeader />

      <section className="tp-hero">
        <div className="tp-hero-inner tp-hero-inner--simple">
          <div className="tp-hero-copy">
            <p className="tp-eyebrow">Matched opportunities</p>
            <h1>Based on your career preference</h1>
            <p>
              Jobs, internships, and trainings ranked for your preferred roles,
              locations, and experience.
            </p>
          </div>
        </div>
      </section>

      <main className="tp-body">
        {loading ? (
          <section className="tp-panel">
            <p className="text-muted">Loading matches…</p>
          </section>
        ) : !matches.has_preference ? (
          <section className="tp-panel">
            <div className="tp-empty">
              <FiUser />
              <h3>Set your career preference</h3>
              <p>
                {matches.message ||
                  "Add preferred roles, locations, and salary to see matched opportunities."}
              </p>
              <Link
                to="/trainee-career-preference"
                className="tp-btn-primary"
              >
                Add preference
              </Link>
            </div>
          </section>
        ) : empty ? (
          <section className="tp-panel">
            <div className="tp-empty">
              <FiBriefcase />
              <h3>No strong matches yet</h3>
              <p>
                Try broadening preferred roles or locations, or browse the full
                catalogs.
              </p>
              <div className="d-flex gap-2 flex-wrap justify-content-center">
                <Link to="/jobs" state={catalogState} className="tp-btn-outline">
                  Browse jobs
                </Link>
                <Link
                  to="/internships"
                  state={catalogState}
                  className="tp-btn-outline"
                >
                  Browse internships
                </Link>
                <Link
                  to="/courses"
                  state={catalogState}
                  className="tp-btn-outline"
                >
                  Browse trainings
                </Link>
              </div>
            </div>
          </section>
        ) : (
          <>
            {matches.jobs.length > 0 ? (
              <section className="tp-panel">
                <div className="tp-panel-head">
                  <h2>
                    <FiBriefcase /> Matched jobs ({matches.jobs.length})
                  </h2>
                  <p>Open roles that fit your preference.</p>
                </div>
                <div className="tp-match-grid">
                  {matches.jobs.map((job) => (
                    <article
                      key={`job-${job.job_id}`}
                      className="tp-match-card"
                    >
                      <span className="tp-match-badge">
                        Job · Match {job.match_score}
                      </span>
                      <h3>{job.role || "Role"}</h3>
                      <p>
                        {job.company?.company_name || "Company"}
                        {job.location ? ` · ${job.location}` : ""}
                      </p>
                      {job.match_reasons?.length ? (
                        <small>{job.match_reasons.join(" · ")}</small>
                      ) : null}
                      <Link
                        to={`/jobs/${job.job_id}`}
                        state={catalogState}
                        className="tp-section-link"
                      >
                        View job <FiArrowRight />
                      </Link>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}

            {matches.internships.length > 0 ? (
              <section className="tp-panel">
                <div className="tp-panel-head">
                  <h2>
                    <FiBriefcase /> Matched internships (
                    {matches.internships.length})
                  </h2>
                  <p>Internships aligned with your preference.</p>
                </div>
                <div className="tp-match-grid">
                  {matches.internships.map((item) => (
                    <article
                      key={`intern-${item.internship_id}`}
                      className="tp-match-card"
                    >
                      <span className="tp-match-badge">
                        Internship · Match {item.match_score}
                      </span>
                      <h3>{item.role || "Role"}</h3>
                      <p>
                        {item.company?.company_name || "Company"}
                        {item.location ? ` · ${item.location}` : ""}
                      </p>
                      {item.match_reasons?.length ? (
                        <small>{item.match_reasons.join(" · ")}</small>
                      ) : null}
                      <Link
                        to={`/internships/${item.internship_id}`}
                        state={catalogState}
                        className="tp-section-link"
                      >
                        View internship <FiArrowRight />
                      </Link>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}

            {matches.trainings.length > 0 ? (
              <section className="tp-panel">
                <div className="tp-panel-head">
                  <h2>
                    <FiBookOpen /> Matched trainings (
                    {matches.trainings.length})
                  </h2>
                  <p>Courses and batches that match your goals.</p>
                </div>
                <div className="tp-match-grid">
                  {matches.trainings.map((batch) => {
                    const courseName =
                      batch.course_material?.course?.course_name ||
                      batch.batch_name ||
                      "Training";
                    return (
                      <article
                        key={`batch-${batch.batch_id}`}
                        className="tp-match-card"
                      >
                        <span className="tp-match-badge">
                          Training · Match {batch.match_score}
                        </span>
                        <h3>{courseName}</h3>
                        <p>
                          {batch.mode || "Mode"}
                          {batch.venue_address
                            ? ` · ${batch.venue_address}`
                            : ""}
                        </p>
                        {batch.match_reasons?.length ? (
                          <small>{batch.match_reasons.join(" · ")}</small>
                        ) : null}
                        <Link
                          to={`/courses/${batch.batch_id}`}
                          state={catalogState}
                          className="tp-section-link"
                        >
                          View training <FiArrowRight />
                        </Link>
                      </article>
                    );
                  })}
                </div>
              </section>
            ) : null}
          </>
        )}
      </main>

      <Footers />
    </div>
  );
}
